import { createUuid,CustomError } from '@common/utils';
import { queryOptions } from '@tanstack/react-query';
import ky from 'ky';

import { LOGIN_TYPE } from '@/api';
import { API_ROUTES, ENV } from '@/constants';

export const authQueryOptionsKeys = {
  socialLogin: createUuid(),
};

interface SocialLoginProps {
  state?: string | null;
  code?: string | null;
}

export interface SocialLoginResult {
  socialLoginToken: string;
  loginType: LOGIN_TYPE;
}

interface LoginResponse {
  access_token: string;
}

export const authQueryOptions = {
  socialLogin: ({ code, state }: SocialLoginProps) => {
    const enabled = !!(code && state);

    return queryOptions({
      queryKey: [authQueryOptionsKeys.socialLogin, enabled, state],
      staleTime: 0,
      enabled,
      queryFn: async (): Promise<SocialLoginResult> => {
        if (!enabled)
          throw new CustomError({
            return_message: 'socialLoginProps 가 없습니다.',
          });

        const decodedState = window.atob(state);

        const { type, redirectPath } = JSON.parse(decodedState);

        switch (type) {
          //! ⚠️ Kakao는 client secret이 없어서 front에서 직접 요청 가능
          case 'kakao': {
            const { access_token } = await ky
              .post('https://kauth.kakao.com/oauth/token', {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                  code,
                  state,
                  redirect_uri: `${ENV.NEXT_PUBLIC_DOMAIN}${redirectPath}`,
                  grant_type: 'authorization_code',
                  client_id: ENV.NEXT_PUBLIC_KAKAO_CLIENT_ID,
                }),
              })
              .json<LoginResponse>();

            return {
              loginType: LOGIN_TYPE.KAKAO,
              socialLoginToken: access_token,
            };
          }

          case 'naver': {
            const { access_token } = await ky
              .post(API_ROUTES.NAVER_AUTH.pathname, {
                json: {
                  code,
                  state,
                },
              })
              .json<LoginResponse>();

            return {
              loginType: LOGIN_TYPE.NAVER,
              socialLoginToken: access_token,
            };
          }

          case 'google': {
            const { access_token } = await ky
              .post(API_ROUTES.GOOGLE_AUTH.pathname, {
                json: {
                  code,
                  state,
                },
              })
              .json<LoginResponse>();

            return {
              loginType: LOGIN_TYPE.GOOGLE,
              socialLoginToken: access_token,
            };
          }

          case 'apple': {
            const { id_token } = await ky
              .post(API_ROUTES.APPLE_TOKEN.pathname, {
                json: {
                  code,
                  state,
                },
              })
              .json<{ id_token: string }>();

            return {
              loginType: LOGIN_TYPE.APPLE,
              socialLoginToken: id_token,
            };
          }

          default:
            throw new Error('잘못된 로그인 타입입니다.');
        }
      },
    });
  },
};
